import React, { useCallback, useEffect, useState } from "react";
import ReactSelect from "react-select";
import CumulativeTestReportService, {
  IdName,
} from "../../_services/CumulativeTestReportService";
import "./cumulative-test-report.css";

interface Option {
  value: number;
  label: string;
}

const toOptions = (items: IdName[]): Option[] =>
  items.map((i) => ({ value: i.id, label: i.name }));

const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: 38, fontSize: 14 }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
  // Render the menu in a body-level portal so it floats above the card / page
  // instead of being clipped or pushing the layout down.
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  multiValue: (base: any) => ({ ...base, maxWidth: 200 }),
};

// Portal the menu to <body> to escape any overflow/stacking-context clipping.
const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;

/**
 * Cumulative Test Report — iteach port of the Java JSP
 * (studentTestReport.do?reqCode=preCumulativeTestReportNew).
 *
 * Behaviour mirrors the JSP exactly:
 *  - Selecting centre(s) reloads the Courses dropdown, then the Tests dropdown.
 *  - Changing year / course / test category / test type reloads the Tests dropdown.
 *  - Tests are required; Search builds the payload and POSTs to the download
 *    endpoint, then shows the "Click here to download" link (or the error).
 */
const CumulativeTestReportPage: React.FC = () => {
  // Static dropdown data
  const [schools, setSchools] = useState<IdName[]>([]);
  const [years, setYears] = useState<IdName[]>([]);
  const [categories, setCategories] = useState<IdName[]>([]);
  const [testTypes, setTestTypes] = useState<IdName[]>([]);

  // Filter-driven dropdown data
  const [courses, setCourses] = useState<IdName[]>([]);
  const [tests, setTests] = useState<IdName[]>([]);

  // Selections
  const [centerIds, setCenterIds] = useState<number[]>([]);
  const [yearIds, setYearIds] = useState<number[]>([]);
  const [courseIds, setCourseIds] = useState<number[]>([]);
  const [testCategoryId, setTestCategoryId] = useState<number>(-1);
  const [testTypeId, setTestTypeId] = useState<number>(-1);
  const [testIds, setTestIds] = useState<number[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [testsError, setTestsError] = useState("");
  const [resultError, setResultError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  // Load static dropdowns on mount (mirrors JSP server-side page-load).
  useEffect(() => {
    CumulativeTestReportService.getFilters()
      .then((d) => {
        setSchools(d.schools);
        setYears(d.years);
        setCategories(d.categories);
        setTestTypes(d.testTypes);
      })
      .catch(() => {
        setSchools([]);
        setYears([]);
        setCategories([]);
        setTestTypes([]);
      });
  }, []);

  // filterTestDropdown() — reload Tests for the current filter selection.
  const reloadTests = useCallback(
    (next: {
      centerIds: number[];
      yearIds: number[];
      courseIds: number[];
      testCategoryId: number;
      testTypeId: number;
    }) => {
      setTestsError("");
      setLoading(true);
      CumulativeTestReportService.getTestsByFilters({
        centerIds: next.centerIds,
        yearIds: next.yearIds,
        courseIds: next.courseIds,
        testCategoryId: next.testCategoryId > 0 ? next.testCategoryId : 0,
        testTypeId: next.testTypeId > 0 ? next.testTypeId : 0,
      })
        .then((res) => {
          setTests(res.tests);
          // Drop any selected tests that are no longer in the list.
          setTestIds((prev) => prev.filter((id) => res.tests.some((t) => t.id === id)));
          if (res.error) setTestsError(res.error);
        })
        .catch(() => {
          setTests([]);
          setTestsError("Failed to load tests.");
        })
        .finally(() => setLoading(false));
    },
    [],
  );

  // Load the Course dropdown. No centre → all courses; centre(s) → related.
  const loadCourses = useCallback((nextCenterIds: number[]) => {
    CumulativeTestReportService.getCoursesByCenter(nextCenterIds)
      .then((list) => {
        setCourses(list);
        // Drop any selected courses no longer in the list.
        setCourseIds((prev) => prev.filter((id) => list.some((c) => c.id === id)));
      })
      .catch(() => setCourses([]));
  }, []);

  // Load all courses once on mount (centre defaults to "All Centres").
  useEffect(() => {
    loadCourses([]);
  }, [loadCourses]);

  const handleCentersChange = (selected: readonly Option[]) => {
    const ids = selected ? selected.map((s) => s.value) : [];
    setCenterIds(ids);
    // Reload courses for the new centre selection (empty → all courses).
    loadCourses(ids);
    // Centre is part of the test filter too — refresh tests.
    reloadTests({ centerIds: ids, yearIds, courseIds: [], testCategoryId, testTypeId });
    setCourseIds([]);
  };

  const handleYearsChange = (selected: readonly Option[]) => {
    const ids = selected ? selected.map((s) => s.value) : [];
    setYearIds(ids);
    if (ids.length > 0) {
      reloadTests({ centerIds, yearIds: ids, courseIds, testCategoryId, testTypeId });
    }
  };

  const handleCoursesChange = (selected: readonly Option[]) => {
    const ids = selected ? selected.map((s) => s.value) : [];
    setCourseIds(ids);
    if (ids.length > 0) {
      reloadTests({ centerIds, yearIds, courseIds: ids, testCategoryId, testTypeId });
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setTestCategoryId(id);
    if (id > 0) {
      reloadTests({ centerIds, yearIds, courseIds, testCategoryId: id, testTypeId });
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setTestTypeId(id);
    if (id > 0) {
      reloadTests({ centerIds, yearIds, courseIds, testCategoryId, testTypeId: id });
    }
  };

  // search() — validate tests, build payload, POST, render the result.
  const handleSearch = () => {
    setResultError("");
    setDownloadUrl("");
    setTestsError("");

    if (testIds.length === 0) {
      setTestsError("Please choose proper tests");
      return;
    }

    setLoading(true);
    CumulativeTestReportService.download({
      centerIds,
      yearIds,
      courseIds,
      testCategoryId: testCategoryId > 0 ? testCategoryId : 0,
      testIds: testIds.join(","),
    })
      .then((res) => {
        if (res.success && res.url) {
          setDownloadUrl(res.url);
        } else {
          setResultError(res.error || "Failed to generate report.");
        }
      })
      .catch((e: any) => {
        setResultError(e?.response?.data?.error || e?.message || "Failed to generate report.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="cumulative-test-report">
      {loading && (
        <div className="ctr-loader-overlay">
          <div className="ctr-loader" />
        </div>
      )}

      <div className="ctr-card">
        <div className="ctr-card-header">
          <h2>Cumulative Test Report</h2>
        </div>

        <div className="ctr-card-body">
          <div className="ctr-filter-row">
            <div className="ctr-field">
              <label>Centre</label>
              <ReactSelect
                isMulti
                options={toOptions(schools)}
                value={toOptions(schools).filter((o) => centerIds.includes(o.value))}
                onChange={handleCentersChange as any}
                placeholder="All Centres"
                styles={selectStyles}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
              />
            </div>

            <div className="ctr-field">
              <label>Academic Year</label>
              <ReactSelect
                isMulti
                options={toOptions(years)}
                value={toOptions(years).filter((o) => yearIds.includes(o.value))}
                onChange={handleYearsChange as any}
                placeholder="All Years"
                styles={selectStyles}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
              />
            </div>

            <div className="ctr-field">
              <label>Course</label>
              <ReactSelect
                isMulti
                options={toOptions(courses)}
                value={toOptions(courses).filter((o) => courseIds.includes(o.value))}
                onChange={handleCoursesChange as any}
                placeholder="All Courses"
                styles={selectStyles}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
              />
            </div>

            <div className="ctr-field">
              <label>Test Category</label>
              <select
                className="form-control ctr-select"
                value={testCategoryId}
                onChange={handleCategoryChange}
              >
                <option value={-1}>--select--</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ctr-field">
              <label>Test Type</label>
              <select
                className="form-control ctr-select"
                value={testTypeId}
                onChange={handleTypeChange}
              >
                <option value={-1}>--select--</option>
                {testTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ctr-field">
              <label>
                Tests<sup className="ctr-required">*</sup>
              </label>
              <ReactSelect
                isMulti
                options={toOptions(tests)}
                value={toOptions(tests).filter((o) => testIds.includes(o.value))}
                onChange={(selected: any) =>
                  setTestIds(selected ? selected.map((s: Option) => s.value) : [])
                }
                placeholder="Select tests"
                styles={selectStyles}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
              />
              {testsError && <p className="ctr-tests-validation">{testsError}</p>}
            </div>
          </div>

          <div className="ctr-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={loading}
            >
              <i className="fa fa-search" style={{ marginRight: 6 }} />
              Search
            </button>
          </div>

          <hr />

          <div className="ctr-result">
            {downloadUrl && (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                Click here to download
              </a>
            )}
            {resultError && <p className="ctr-result-error">{resultError}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CumulativeTestReportPage;
